import React, { useState } from "react";
import {
  getFirestore,
  collection,
  query,
  where,
  getDocs,
  addDoc,
  Timestamp,
} from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { db, functions } from "../firebase/config";

const AddAdmin = ({ onAdminAdded }) => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(false);

  const handleAddAdmin = async () => {
    setStatus("");
    setLoading(true);

    try {
      if (!email || !name) {
        setStatus("❌ Please fill all fields.");
        setLoading(false);
        return;
      }

      // Step 1: Check if admin already exists
      const q = query(collection(db, "admin"), where("email", "==", email));
      const snapshot = await getDocs(q);

      if (!snapshot.empty) {
        setStatus("⚠️ Admin already exists.");
        setLoading(false);
        return;
      }

      // Step 2: Add admin to Firestore
      await addDoc(collection(db, "admin"), {
        email,
        name,
        role: "admin",
        createdAt: Timestamp.now(),
      });

      // Step 3: (Optional) Call Cloud Function to set custom claim
      try {
        const assignAdmin = httpsCallable(functions, "assignAdminRole");
        await assignAdmin({ email });
        console.log("✅ Custom claim set via Cloud Function");
      } catch (functionError) {
        console.warn("⚠️ Cloud Function failed, but admin added to Firestore:", functionError);
        // Don't fail the whole operation if Cloud Function fails
      }

      setStatus("✅ Admin added successfully!");
      setName("");
      setEmail("");
      
      // Notify parent component to refresh admin list
      if (onAdminAdded) {
        onAdminAdded();
      }
    } catch (error) {
      console.error("Error:", error);
      setStatus("❌ " + error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-md mx-auto mt-6 p-6 bg-white rounded-xl shadow-md border border-gray-200">
      <h2 className="text-xl font-bold mb-4 text-gray-800">➕ Add New Admin</h2>

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Full Name
          </label>
          <input
            type="text"
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
            placeholder="Enter full name"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Email Address
          </label>
          <input
            type="email"
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
            placeholder="Enter email address"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>

        <button
          onClick={handleAddAdmin}
          disabled={loading}
          className="w-full bg-green-600 text-white py-3 px-4 rounded-lg hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors duration-200 font-medium"
        >
          {loading ? (
            <span className="flex items-center justify-center">
              <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Adding Admin...
            </span>
          ) : (
            "Add Admin"
          )}
        </button>

        {status && (
          <div className={`mt-4 p-3 rounded-lg text-sm ${
            status.includes("✅") 
              ? "bg-green-50 text-green-700 border border-green-200" 
              : status.includes("⚠️") 
                ? "bg-yellow-50 text-yellow-700 border border-yellow-200"
                : "bg-red-50 text-red-700 border border-red-200"
          }`}>
            {status}
          </div>
        )}
      </div>
    </div>
  );
};

export default AddAdmin; 